/**
 * Cliente HTTP. Cookies HTTP-only (super_session) são enviados automaticamente
 * pelo browser quando `credentials: 'include'`.
 *
 * 401 → dispara redirect pra /login (handler global). Throw `ApiError` com
 * problem details pra a UI mostrar toast.
 */

export interface ProblemDetails {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  code: string;
  trace_id?: string;
  context?: Record<string, unknown>;
}

export class ApiError extends Error {
  constructor(public readonly problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
  }
}

export interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Bearer token pra fluxo TOTP (setup_token, challenge_token). */
  bearerToken?: string;
  /** Suprime redirect global em 401. Útil pro próprio /me check no boot. */
  silent401?: boolean;
}

const ON_401_HANDLERS = new Set<() => void>();
export function onUnauthorized(handler: () => void): () => void {
  ON_401_HANDLERS.add(handler);
  return () => ON_401_HANDLERS.delete(handler);
}

export async function apiRequest<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = {
    accept: 'application/json, application/problem+json',
  };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.bearerToken) headers.authorization = `Bearer ${opts.bearerToken}`;

  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
    ...(opts.body !== undefined && { body: JSON.stringify(opts.body) }),
  });

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    if (res.status === 401 && !opts.silent401) {
      for (const h of ON_401_HANDLERS) h();
    }
    const problem: ProblemDetails =
      typeof parsed === 'object' && parsed !== null && 'code' in parsed
        ? (parsed as ProblemDetails)
        : {
            title: `HTTP ${res.status}`,
            status: res.status,
            code: 'HTTP_ERROR',
            detail: typeof parsed === 'string' ? parsed : undefined,
          };
    throw new ApiError(problem);
  }

  return parsed as T;
}
